import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react'
import { api, type MeResponse } from '../lib/api'
import { AUTH_HOME, clearReviewerToken } from '../lib/hmvmAuth'

export type AuthVia = 'sso' | 'tg'

export type ReviewSession = {
  sub: string
  name: string
  role: string
  auth_via: AuthVia
}

type ReviewSessionContextValue = {
  session: ReviewSession | null
  setFromMe: (me: MeResponse) => void
  logout: () => Promise<void>
}

const ReviewSessionContext = createContext<ReviewSessionContextValue | null>(null)

export function normalizeAuthVia(value?: string | null): AuthVia {
  const raw = (value || '').trim().toLowerCase()
  if (raw === 'tg' || raw === 'telegram' || raw === 'qr') return 'tg'
  return 'sso'
}

export function sessionFromMe(me: MeResponse): ReviewSession | null {
  const name = (me.name || me.sub || '').trim()
  if (!name && !me.role) return null
  return {
    sub: me.sub || '',
    name: name || me.sub || '',
    role: me.role || '',
    auth_via: normalizeAuthVia(me.auth_via),
  }
}

export function ReviewSessionProvider({
  children,
  initialSession = null,
}: {
  children: ReactNode
  initialSession?: ReviewSession | null
}) {
  const [session, setSession] = useState<ReviewSession | null>(initialSession)

  const setFromMe = useCallback((me: MeResponse) => {
    setSession(sessionFromMe(me))
  }, [])

  const logout = useCallback(async () => {
    try {
      await api.logout()
    } catch {
      /* cookie clear is best-effort */
    }
    clearReviewerToken()
    setSession(null)
    window.location.assign(AUTH_HOME)
  }, [])

  const value = useMemo(
    () => ({ session, setFromMe, logout }),
    [session, setFromMe, logout],
  )

  return <ReviewSessionContext.Provider value={value}>{children}</ReviewSessionContext.Provider>
}

export function useReviewSession(): ReviewSessionContextValue {
  const ctx = useContext(ReviewSessionContext)
  if (!ctx) {
    return {
      session: null,
      setFromMe: () => undefined,
      logout: async () => undefined,
    }
  }
  return ctx
}
